import React, { useState } from 'react';
import { Popover, Button, Typography } from 'antd';
import { formatNumberWithCommas } from 'utils/helperMethods';
import RangeInputCustom from './RangeInput';

const { Text } = Typography;

interface Props {
    minVal: number | null;
    maxVal: number | null;
    onMaxChange: (val: number) => void;
    onMinChange: (val: number) => void;
}

const RangeInputWithPopover: React.FC<Props> = ({ minVal, maxVal, onMaxChange, onMinChange }) => {
    const [open, setOpen] = useState(false);
    const [tempMin, setTempMin] = useState<number | null>(minVal);
    const [tempMax, setTempMax] = useState<number | null>(maxVal);
    const [error, setError] = useState('');

    const handleOpenChange = (visible: boolean) => {
        setOpen(visible);
        if (visible) {
            setTempMin(minVal);
            setTempMax(maxVal);
            setError('');
        }
    };

    const validate = () => {
        // Only validate if both values are provided and are valid numbers
        if (tempMin != null && tempMax != null && !isNaN(tempMin) && !isNaN(tempMax)) {
            if (tempMin >= tempMax) {
                setError('Minimum value should be less than maximum value.');
                return false;
            }
        }
        setError('');
        return true;
    };

    const handleUpdate = () => {
        if (!validate()) return;
        // Only update if both values are valid numbers
        if (tempMin != null && tempMax != null && !isNaN(tempMin) && !isNaN(tempMax)) {
            onMinChange(tempMin);
            onMaxChange(tempMax);
            setOpen(false);
        }
    };

    const content = (
        <div className="space-y-2 w-[300px] m-auto">
            <RangeInputCustom
                minVal={tempMin}
                maxVal={tempMax}
                onMinChange={(val) => {
                    setTempMin(val);
                    if (error) validate();
                }}
                onMaxChange={(val) => {
                    setTempMax(val);
                    if (error) validate();
                }}
                parentClassName="gap-2"
                inputClassName="w-full"
            />
            {error && <div className="text-redAlt text-xs">{error}</div>}
            <div className="text-right mt-2">
                <button
                    onClick={handleUpdate}
                    disabled={!!error}
                    className={`${
                        !!error ? 'bg-backgroundGreyAlt1 text-backgroundGreyAlt2' : 'bg-purpleFont text-white'
                    } px-2 py-1 rounded`}
                >
                    Update
                </button>
            </div>
        </div>
    );

    const formattedRange =
        minVal != null && maxVal != null
            ? `${formatNumberWithCommas(minVal, 2)}   -   ${formatNumberWithCommas(maxVal, 2)}`
            : 'Click to enter range';

    return (
        <Popover content={content} title="Edit Range" trigger="click" open={open} onOpenChange={handleOpenChange}>
            <div
                className={`border border-backgroundGreyAlt8 rounded px-3 py-1 cursor-pointer hover:bg-bgBackground  w-full text-center ${
                    open ? 'border border-purpleFont bg-bgBackground' : ''
                }`}
                onClick={() => setOpen(true)}
            >
                {formattedRange}
            </div>
        </Popover>
    );
};

export default RangeInputWithPopover;
